import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { authFetch } from '@/utils/authFetch';

/** What the shell's sidebar badges read: GET /api/ops/summary. */
export interface OpsSummary {
  checkedAt: string;
  jobs: { total: number; failing: number; stale: number; unknown: number; paused: number };
  mcp: { total: number; down: number };
  problems: number;
  openProposals: number;
  items: { kind: 'job' | 'mcp'; id: string; name: string; state: string; detail?: string | null }[];
}
/**
 * What one module says needs attention right now, shown as that module's badge in the sidebar (round 6:
 * "I still don't see the yellow overdue orders in the left side menu; you can remove the tile from the top
 * right" — the top bar's pill is gone and the count is a sidebar badge, next to the module it belongs to).
 *
 * `stale` marks a registration whose module has been unmounted: the last known count, kept so the badge does
 * not blink out the moment you navigate away from the module that counted it. Nothing clears it but a fresh
 * registration.
 *
 * `items` and `open()` are the pill's old interface. Nothing in the shell calls them now — the sidebar item
 * navigates to the module, and the module's own header owns the details modal — but they stay part of the
 * registration so a module can keep offering them (the Production Scheduler still passes `open`) and so a
 * later surface can use them without every caller changing.
 */
export interface ModuleAttention {
  count: number;
  label: string;
  items?: { id: string; name: string; state: string }[];
  open?: () => void;
  stale?: boolean;
}
export type EnvKind = 'test' | 'prod' | 'local' | 'unknown';
export interface ShellEnv { name: string; kind: EnvKind }

const COLLAPSED_KEY = 'bridgeos.shell.collapsed';
const POLL_MS = 60_000;

/** Environment from the build (mesctl sets VITE_MES_ENV_NAME/KIND); falls back to the hostname or local dev. */
export function resolveEnv(env: Record<string, string | boolean | undefined> = import.meta.env as unknown as Record<string, string | boolean | undefined>, hostname: string = typeof location !== 'undefined' ? location.hostname : ''): ShellEnv {
  const name = String(env.VITE_MES_ENV_NAME || '').trim();
  const kind = String(env.VITE_MES_ENV_KIND || '').trim() as EnvKind;
  if (name && (kind === 'test' || kind === 'prod')) return { name, kind };
  const m = /^[a-z0-9]+-(test\d*|prod\d*)\./i.exec(hostname);
  if (m) return { name: m[1].toLowerCase(), kind: m[1].toLowerCase().startsWith('prod') ? 'prod' : 'test' };
  if (env.DEV || /^(localhost|127\.)/.test(hostname)) return { name: 'local', kind: 'local' };
  return { name: name || hostname || 'unknown', kind: kind === 'test' || kind === 'prod' ? kind : 'unknown' };
}

export const useShellStore = defineStore('shell', () => {
  const collapsed = ref<boolean>((() => { try { return localStorage.getItem(COLLAPSED_KEY) === '1'; } catch { return false; } })());
  const env = ref<ShellEnv>(resolveEnv());
  const summary = ref<OpsSummary | null>(null);
  const summaryError = ref<string | null>(null);
  /** Per-module attention registrations (see `composables/useModuleAttention`); the sidebar badges read these. */
  const moduleAttention = ref<Record<string, ModuleAttention | null>>({});
  let timer: ReturnType<typeof setInterval> | null = null;

  const attention = computed(() => (summary.value ? summary.value.problems + summary.value.mcp.down : 0));
  const badges = computed<Record<string, number>>(() => summary.value ? { ops: summary.value.problems + summary.value.mcp.down, agentConsole: summary.value.openProposals } : {});

  function setModuleAttention(moduleId: string, a: ModuleAttention | null) { moduleAttention.value = { ...moduleAttention.value, [moduleId]: a }; }

  function setCollapsed(on: boolean) { collapsed.value = on; try { localStorage.setItem(COLLAPSED_KEY, on ? '1' : '0'); } catch { /* per-viewer convenience only */ } }
  function toggleCollapsed() { setCollapsed(!collapsed.value); }

  async function refreshSummary(fetchImpl: typeof authFetch = authFetch) {
    try {
      const base = import.meta.env.VITE_API_BASE_URL || '';
      const res = await fetchImpl(`${base}/api/ops/summary`);
      if (res.status === 401 || res.status === 403) { summary.value = null; summaryError.value = null; return; } // not for this user; no badges
      if (!res.ok) throw new Error(`summary ${res.status}`);
      summary.value = await res.json(); summaryError.value = null;
    } catch (e) { summaryError.value = e instanceof Error ? e.message : String(e); }
  }
  function startPolling() {
    if (timer) return;
    void refreshSummary();
    timer = setInterval(() => { if (typeof document === 'undefined' || !document.hidden) void refreshSummary(); }, POLL_MS);
  }
  function stopPolling() { if (timer) clearInterval(timer); timer = null; summary.value = null; }

  return { collapsed, env, summary, summaryError, moduleAttention, attention, badges, setModuleAttention, setCollapsed, toggleCollapsed, refreshSummary, startPolling, stopPolling };
});
